import { useInjection } from 'inversify-react';
import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Spinner } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { BatchProgress } from '../components/digiinsulin/BatchProgress';
import { parseResponseData } from '../components/digiinsulin/utils';
import { API } from '../services/HTTP';

interface DigiInsulinBatchDetail {
    id?: string;
    SK?: string;
    status: string;
    totalCount: number;
    completedCount: number;
    failedCount: number;
    created_at: string;
    reportS3Key?: string;
}

export function DigiInsulinBatchPage() {
    const { id } = useParams<{ id: string }>();
    const api: API = useInjection<API>('API');
    const [batch, setBatch] = useState<DigiInsulinBatchDetail | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [reportError, setReportError] = useState<string | null>(null);
    const [isDownloadingReport, setIsDownloadingReport] = useState(false);
    const isTerminalStatus = batch?.status === 'complete' || batch?.status === 'failed';

    const loadBatch = useCallback(async (showLoadingSpinner = false) => {
        if (!id) {
            setBatch(null);
            setIsLoading(false);
            return;
        }

        if (showLoadingSpinner) {
            setIsLoading(true);
        }

        try {
            const response = await api.get(`digiinsulin/results/${encodeURIComponent(id)}`);
            setBatch(parseResponseData<DigiInsulinBatchDetail>(response.data));
            setLoadError(null);
        } catch (error) {
            console.error('Failed to load DigiInsulin batch:', error);
            setBatch(null);
            setLoadError('Failed to load batch. Please try again.');
        } finally {
            if (showLoadingSpinner) {
                setIsLoading(false);
            }
        }
    }, [api, id]);

    useEffect(() => {
        void loadBatch(true);
    }, [loadBatch]);

    useEffect(() => {
        if (!id || !batch || isTerminalStatus) {
            return;
        }

        const intervalId = window.setInterval(() => {
            void loadBatch();
        }, 5000);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [batch, id, isTerminalStatus, loadBatch]);

    const handleDownloadReport = useCallback(async () => {
        if (!id || !batch?.reportS3Key || isDownloadingReport) {
            return;
        }

        setIsDownloadingReport(true);
        setReportError(null);

        try {
            const response = await api.get(`digiinsulin/report/${encodeURIComponent(id)}`);
            const payload = parseResponseData<{ reportUrl?: string; url?: string }>(response.data);
            const reportUrl = payload.reportUrl || payload.url;

            if (reportUrl) {
                window.open(reportUrl, '_blank', 'noreferrer');
            } else {
                setReportError('Failed to download batch report. Please try again.');
            }
        } catch (error) {
            console.error('Failed to download DigiInsulin batch report:', error);
            setReportError('Failed to download batch report. Please try again.');
        } finally {
            setIsDownloadingReport(false);
        }
    }, [api, batch?.reportS3Key, id, isDownloadingReport]);

    if (isLoading) {
        return <Spinner animation="border" />;
    }

    if (loadError) {
        return <Alert variant="danger">{loadError}</Alert>;
    }

    if (!batch) {
        return <p className="text-muted">Batch not found.</p>;
    }

    return (
        <>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                <div>
                    <div className="mb-2">
                        <Link to="/digiinsulin">← Back to results</Link>
                    </div>
                    <h1 className="mb-2">Batch Analysis</h1>
                    <p className="text-muted mb-0">Started: {new Date(batch.created_at).toLocaleString()}</p>
                </div>
                {batch.reportS3Key && (
                    <Button variant="link" className="p-0" onClick={handleDownloadReport}>
                        {isDownloadingReport ? 'Downloading report…' : 'Download Batch Report'}
                    </Button>
                )}
            </div>

            {reportError && <Alert variant="danger">{reportError}</Alert>}

            <Card>
                <Card.Body>
                    <BatchProgress
                        totalCount={batch.totalCount}
                        completedCount={batch.completedCount}
                        failedCount={batch.failedCount}
                        status={batch.status}
                    />
                </Card.Body>
            </Card>
        </>
    );
}
